import store from '../store';
import { setFloDetails } from '../Pages/Flo/Flo.reducers';
import { get } from 'lodash-es';
import { getWrapperValue, getThemeDetails } from '../Common/Common.utils';

/**
 * Message structure for preview config updates from parent app
 */
interface PreviewConfigMessage {
	type: 'PREVIEW_CONFIG_UPDATE';
	payload: Record<string, unknown>;
}

/**
 * Handler function type for individual config keys
 */
type ConfigHandler = (value: unknown, floIndex: number) => void;

/**
 * Allowed origins for postMessage communication
 * Add trusted parent origins here
 */
const ALLOWED_ORIGINS = [
	'https://app.floik.com',
	'https://staging.floik.com',
	'http://localhost:3000',
	'http://localhost:3001',
	'http://localhost:5173',
	'http://localhost:4200',
	'https://us.realmofdev.com',
];

/**
 * Check if we're in development mode to allow all origins
 */
const isDevelopment = process.env.NODE_ENV === 'development';

/**
 * Validate if the origin is allowed
 */
function isOriginAllowed(origin: string): boolean {
	if (isDevelopment) {
		return true;
	}
	return ALLOWED_ORIGINS.some(
		(allowed) => origin === allowed || origin.endsWith('.floik.com')
	);
}

/**
 * Apply theme changes to the DOM and Redux state
 */
const applyTheme: ConfigHandler = (value, floIndex) => {
	const theme = value as string;
	const themeColor = getThemeDetails(theme) || 'none';

	// Update floik-flo element theme attribute
	const embeddedElements = document.querySelectorAll('floik-flo');
	if (embeddedElements[floIndex]) {
		embeddedElements[floIndex].setAttribute('theme', theme);
	}

	// Update Redux state
	const state = store.getState();
	const currentFloDetails = get(state, `floPage.flos.${floIndex}.floDetails`, {});
	store.dispatch(
		setFloDetails({
			...currentFloDetails,
			theme,
			floIndex,
		})
	);

	// Update CSS custom properties for theme color
	if (themeColor !== 'none') {
		document.documentElement.style.setProperty('--local-themeColor', themeColor);
	}
};

/**
 * Apply view changes (e.g., 'default', 'compact')
 */
const applyView: ConfigHandler = (value, floIndex) => {
	const view = value as string;

	// Update floik-flo element view attribute
	const embeddedElements = document.querySelectorAll('floik-flo');
	if (embeddedElements[floIndex]) {
		embeddedElements[floIndex].setAttribute('view', view);
	}

	// Update Redux state
	const state = store.getState();
	const currentFloDetails = get(state, `floPage.flos.${floIndex}.floDetails`, {});
	store.dispatch(
		setFloDetails({
			...currentFloDetails,
			view,
			floIndex,
		})
	);
};

/**
 * Apply wrapper changes (e.g., 'rounded', 'square', 'light', 'dark')
 */
const applyWrapper: ConfigHandler = (value, floIndex) => {
	const wrapper = getWrapperValue(value as string);

	// Update floik-flo element wrapper attribute
	const embeddedElements = document.querySelectorAll('floik-flo');
	if (embeddedElements[floIndex]) {
		embeddedElements[floIndex].setAttribute('wrapper', wrapper);
	}

	// Update Redux state
	const state = store.getState();
	const currentFloDetails = get(state, `floPage.flos.${floIndex}.floDetails`, {});
	store.dispatch(
		setFloDetails({
			...currentFloDetails,
			wrapper,
			floIndex,
		})
	);
};

/**
 * Toggle TOC visibility
 */
const applyShowToc: ConfigHandler = (value, floIndex) => {
	const showToc = value === true || value === 'true';

	// Update floik-flo element attribute
	const embeddedElements = document.querySelectorAll('floik-flo');
	if (embeddedElements[floIndex]) {
		embeddedElements[floIndex].setAttribute('show-toc', String(showToc));
	}

	// Update Redux state
	const state = store.getState();
	const currentFloDetails = get(state, `floPage.flos.${floIndex}.floDetails`, {});
	store.dispatch(
		setFloDetails({
			...currentFloDetails,
			'show-toc': showToc,
			floIndex,
		})
	);
};

/**
 * Toggle author visibility
 */
const applyShowAuthor: ConfigHandler = (value, floIndex) => {
	const showAuthor = value === true || value === 'true';

	// Update floik-flo element attribute
	const embeddedElements = document.querySelectorAll('floik-flo');
	if (embeddedElements[floIndex]) {
		embeddedElements[floIndex].setAttribute('show-author', String(showAuthor));
	}

	// Update Redux state
	const state = store.getState();
	const currentFloDetails = get(state, `floPage.flos.${floIndex}.floDetails`, {});
	store.dispatch(
		setFloDetails({
			...currentFloDetails,
			'show-author': showAuthor,
			floIndex,
		})
	);
};

/**
 * Toggle flo title visibility
 */
const applyShowFloTitle: ConfigHandler = (value, floIndex) => {
	const showFloTitle = value === true || value === 'true';

	// Update floik-flo element attribute
	const embeddedElements = document.querySelectorAll('floik-flo');
	if (embeddedElements[floIndex]) {
		embeddedElements[floIndex].setAttribute('show-flo-title', String(showFloTitle));
	}

	// Update Redux state
	const state = store.getState();
	const currentFloDetails = get(state, `floPage.flos.${floIndex}.floDetails`, {});
	store.dispatch(
		setFloDetails({
			...currentFloDetails,
			'show-flo-title': showFloTitle,
			floIndex,
		})
	);
};

/**
 * Toggle header visibility
 */
const applyHideHeader: ConfigHandler = (value, floIndex) => {
	const hideHeader = value === true || value === 'true';

	// Update floik-flo element attribute
	const embeddedElements = document.querySelectorAll('floik-flo');
	if (embeddedElements[floIndex]) {
		embeddedElements[floIndex].setAttribute('hide-header', String(hideHeader));
	}

	// Update Redux state
	const state = store.getState();
	const currentFloDetails = get(state, `floPage.flos.${floIndex}.floDetails`, {});
	store.dispatch(
		setFloDetails({
			...currentFloDetails,
			'hide-header': hideHeader,
			floIndex,
		})
	);
};

/**
 * Toggle auto-play
 */
const applyAutoPlay: ConfigHandler = (value, floIndex) => {
	const autoPlay = value === true || value === 'true';

	// Update floik-flo element attribute
	const embeddedElements = document.querySelectorAll('floik-flo');
	if (embeddedElements[floIndex]) {
		embeddedElements[floIndex].setAttribute('auto-play', String(autoPlay));
	}

	// Update Redux state
	const state = store.getState();
	const currentFloDetails = get(state, `floPage.flos.${floIndex}.floDetails`, {});
	store.dispatch(
		setFloDetails({
			...currentFloDetails,
			'auto-play': autoPlay,
			floIndex,
		})
	);
};

/**
 * Registry of config handlers mapped by key
 */
const configHandlers: Record<string, ConfigHandler> = {
	theme: applyTheme,
	view: applyView,
	wrapper: applyWrapper,
	'show-toc': applyShowToc,
	'show-author': applyShowAuthor,
	'show-flo-title': applyShowFloTitle,
	'hide-header': applyHideHeader,
	'auto-play': applyAutoPlay,
};

/**
 * Process a config update message and apply changes
 */

/**
 * Send PREVIEW_READY message to parent window
 */
export function sendPreviewReady(): void {
	if (window.parent !== window) {
		window.parent.postMessage({ type: 'PREVIEW_READY' }, '*');
	}
}

function processConfigUpdate(payload: Record<string, unknown>, floIndex: number): void {
	let newObj: Record<string, unknown> = {};
	console.log('payload [log]', payload);
	Object.entries(payload).forEach(([key, value]) => {
		if (key === 'wrapper') {
			const wrapperValue = getWrapperValue(value as string) || 'rounded';
			newObj[key] = wrapperValue;
		} else {
			newObj[key] = value;
		}
	});
	const state = store.getState();
	const currentFloDetails = get(state, `floPage.flos.${floIndex}.floDetails`, {});
	if (!newObj.animation) {
		const { animatedTheme, animatedThemeColors, ...restCurrentFloDetails } =
			currentFloDetails;
		newObj = {
			...restCurrentFloDetails,
			...newObj,
		};
	} else {
		newObj = {
			...currentFloDetails,
			...newObj,
		};
	}

	store.dispatch(
		setFloDetails({
			...newObj,
			floIndex,
		})
	);
}

/**
 * Initialize the config message listener
 * @param floIndex - The index of the flo to update (default: 0)
 * @returns Cleanup function to remove the listener
 */
export function initConfigMessageListener(floIndex: number = 0): () => void {
	const handler = (event: MessageEvent) => {
		// Validate origin
		if (!isOriginAllowed(event.origin)) {
			return;
		}

		// Validate message structure
		const data = event.data as PreviewConfigMessage;
		if (!data || data.type !== 'PREVIEW_CONFIG_UPDATE') {
			return;
		}

		// Validate payload exists
		if (!data.payload || typeof data.payload !== 'object') {
			console.warn('[ConfigMessageHandler] Invalid payload received');
			return;
		}

		// Process the config update
		processConfigUpdate(data.payload, floIndex);
	};

	window.addEventListener('message', handler);

	// Send ready signal to parent
	sendPreviewReady();

	// Return cleanup function
	return () => {
		window.removeEventListener('message', handler);
	};
}

/**
 * Register a custom config handler
 * @param key - The config key to handle
 * @param handler - The handler function
 */
export function registerConfigHandler(key: string, handler: ConfigHandler): void {
	configHandlers[key] = handler;
}

/**
 * Get the list of allowed origins
 */
export function getAllowedOrigins(): string[] {
	return [...ALLOWED_ORIGINS];
}

/**
 * Add an allowed origin dynamically
 */
export function addAllowedOrigin(origin: string): void {
	if (!ALLOWED_ORIGINS.includes(origin)) {
		ALLOWED_ORIGINS.push(origin);
	}
}

export default {
	initConfigMessageListener,
	sendPreviewReady,
	registerConfigHandler,
	getAllowedOrigins,
	addAllowedOrigin,
};
